// SMS tab — 2-way conversation thread + in-place composer for the Lead Detail
// center column. Reuses the tenant-scoped /sms/send endpoint and the lead
// payload's messages array (channel === 'sms'); the lead payload polls, so the
// thread refreshes on its own and immediately after a send.

import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Send, Loader2, Check, CheckCheck, AlertCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost } from '../../../lib/api.js';
import { fmtDate, fmtFromNow } from '../../../lib/format.js';
import { useAuth } from '../../../store/auth.js';
import { parseSchedule, resolveMergeTags, smsSegments } from '../../../lib/smsCompose.js';
import { MergeTagBar, SchedulePicker, SmsCounter } from '../../sms/SmsComposeTools.jsx';

export { smsSegments };

const STATUS = {
  sent: { icon: Check, label: 'Sent', cls: 'text-white/40' },
  queued: { icon: Check, label: 'Sending', cls: 'text-white/40' },
  delivered: { icon: CheckCheck, label: 'Delivered', cls: 'text-emerald-300' },
  undelivered: { icon: AlertCircle, label: 'Undelivered', cls: 'text-amber-300' },
  failed: { icon: AlertCircle, label: 'Failed', cls: 'text-red-300' },
};

export default function SmsTab({ leadId, lead, messages = [], onChange }) {
  const qc = useQueryClient();
  const agent = useAuth((st) => st.agent);
  const textareaRef = useRef(null);
  const [schedule, setSchedule] = useState({ on: false, date: '', time: '' });
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const sms = messages
    .filter((m) => (m.channel || 'sms') === 'sms')
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  // Keep the newest message in view as the thread grows.
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [sms.length]);

  async function send() {
    if (!body.trim() || sending) return;
    let scheduledFor;
    if (schedule.on) {
      const { at, error } = parseSchedule(schedule.date, schedule.time);
      if (error) { toast.error(error); return; }
      scheduledFor = at.toISOString();
    }
    setSending(true);
    try {
      await apiPost('/sms/send', { leadId, body: resolveMergeTags(body, lead, agent), ...(scheduledFor ? { scheduledFor } : {}) });
      if (scheduledFor) toast.success(`Text scheduled for ${fmtDate(scheduledFor)}`);
      else toast.success('Sent');
      setBody('');
      setSchedule({ on: false, date: '', time: '' });
      ['lead-messages', 'scheduled-texts'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Send failed');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 p-1">
        {sms.length === 0 && (
          <p className="text-xs text-white/50 p-3 rounded-lg border border-civix-border">
            There is no SMS history between you and this phone number yet.
          </p>
        )}
        {sms.map((m) => {
          const inbound = m.direction === 'inbound';
          const st = !inbound ? STATUS[m.status] : null;
          const StIcon = st?.icon;
          return (
            <div
              key={m._id}
              className={`max-w-[80%] rounded-lg px-3 py-2 text-xs ${
                inbound
                  ? 'bg-slate-800/80 border border-civix-border self-start'
                  : 'bg-civix-purple/25 border border-civix-purple/40 self-end'
              }`}
            >
              <div className="text-white/90 whitespace-pre-wrap wrap-break-word">{m.body}</div>
              <div className="text-[10px] text-white/40 mt-1 flex items-center gap-1.5 justify-end" title={m.createdAt ? fmtDate(m.createdAt) : ''}>
                <span>{fmtFromNow(m.createdAt)}</span>
                {st && <span className={`flex items-center gap-0.5 ${st.cls}`}><StIcon className="w-3 h-3" />{st.label}</span>}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="border-t border-civix-border pt-2 space-y-2">
        <MergeTagBar textareaRef={textareaRef} value={body} onChange={setBody} />
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send(); }}
            placeholder="Type an SMS… (Ctrl+Enter to send)"
            rows={3}
            className="flex-1 resize-none rounded-lg bg-civix-panel border border-civix-border
                       px-3 py-2 text-xs text-white/85 focus:border-civix-purple/60 outline-hidden"
          />
          <button
            onClick={send}
            disabled={sending || !body.trim()}
            className="btn-primary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : schedule.on ? <Clock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            {schedule.on ? 'Schedule Text' : 'Send Now'}
          </button>
        </div>
        {/#(firstName|lastName|fullName|phone|state|agentName)\b/i.test(body) && lead && (
          <p className="text-[11px] text-white/50 rounded-lg bg-civix-panel border border-civix-border px-2 py-1">
            Preview: {resolveMergeTags(body, lead, agent)}
          </p>
        )}
        <SmsCounter text={resolveMergeTags(body, lead, agent)} />
        <SchedulePicker value={schedule} onChange={setSchedule} />
      </div>
    </div>
  );
}
